import { ref, watch } from 'vue';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import { SlimOrganization } from '@ilteducation/my-account-types';
import {
  checkAssociationDeletePermission,
  checkAssociationUpdatePermission,
  Reason,
} from '@ilteducation/customer-mgmt-permissions';
import { usePermissions } from '../../../composables/usePermissions';
import { useCurrentOrganization } from '../../../composables/useCurrentOrganization';
import { useUserRoles } from '../../../composables/useUserRoles';
import { computed } from 'vue';

/**
 * When not a single role can be created, the four denials rarely share a reason. Surface the one
 * closest to something the caller can act on rather than whichever role happened to be checked
 * first: `mfa_required` is one escalated login away, `only_super_user_permitted` is a dead end.
 */
const REASON_PRIORITY: ReadonlyArray<Reason> = [
  'mfa_required',
  'only_group_manager_permitted',
  'only_admin_permitted',
  'subscription_required',
  'only_super_user_permitted',
];

const leastRestrictiveReason = (reasons: Reason[]): Reason => {
  const rank = (reason: Reason) => {
    const index = REASON_PRIORITY.indexOf(reason);
    return index === -1 ? REASON_PRIORITY.length : index;
  };
  return [...reasons].sort((a, b) => rank(a) - rank(b))[0] ?? 'not_permitted';
};

export const useOrganizationsTableChecks = (
  targetUser: CustomerMgmtTypes.GetUserResponse,
  isSelected: ({ uid }: { uid: string }) => boolean,
) => {
  const { permissionContext, reporterWithReason, getCallerAssociation, createTargetAssociation } = usePermissions();
  const { organizations } = useCurrentOrganization();
  const { getRoleOptionsToCreateWith } = useUserRoles();

  type PermissionResult = ReturnType<typeof reporterWithReason>;
  type Entities = [string, PermissionResult];

  const callerAdminEmployeeOrganizations = computed(() =>
    organizations.value.filter((association) => ['employee', 'admin'].includes(association.role)),
  );

  const createItemsForbiddenMap = async (entities: Promise<Entities[]>): Promise<Map<string, Reason>> => {
    const awaitedEntities = await entities;
    return new Map(
      awaitedEntities
        .filter(([_uid, result]) => result.allowed === false)
        .map(([uid, result]) => [uid, result.reason as Reason]),
    );
  };

  /**
   * A row is locked only when the caller may create no role at all in that organization.
   * Probing one role would misreport organizations where some roles are blocked and others are
   * not — students cannot be created in preschools, but employees, admins and guardians can.
   */
  const isUsersForbiddenToCreate = async (organization: SlimOrganization): Promise<PermissionResult> => {
    const options = await getRoleOptionsToCreateWith(organization);
    if (options.some((option) => option.allowed)) {
      return { allowed: true, reason: undefined };
    }

    const reasons = options.map((option) => option.reason).filter((reason): reason is Reason => reason !== undefined);
    return { allowed: false, reason: leastRestrictiveReason(reasons) };
  };

  const isAssociationUpdatable = async (user: CustomerMgmtTypes.GetUserResponse, organization: SlimOrganization) => {
    const existingAssociation = user.associations.find(
      (association) => association.schoolUnit.uid === organization.uid,
    );
    if (!existingAssociation) {
      console.error('Association to update not found for organization', organization.uid);
      return { allowed: false, reason: 'not_permitted' };
    }

    return checkAssociationUpdatePermission(
      permissionContext.value,
      createTargetAssociation(user, existingAssociation),
      {
        role: existingAssociation.isAdministrator ? 'admin' : existingAssociation.role,
      },
      getCallerAssociation,
      reporterWithReason,
    );
  };

  const isUserAssociationForbiddenToDelete = async (
    user: CustomerMgmtTypes.GetUserResponse,
    organization: SlimOrganization,
  ) => {
    const existingAssociation = user.associations.find(
      (association) => association.schoolUnit.uid === organization.uid,
    );
    if (!existingAssociation) {
      console.error('Association to delete not found for organization', organization.uid);
      return { allowed: false, reason: 'not_permitted' } satisfies PermissionResult;
    }

    return checkAssociationDeletePermission(
      permissionContext.value,
      createTargetAssociation(user, existingAssociation),
      getCallerAssociation,
      reporterWithReason,
    );
  };

  const itemsForbiddenToCreate = ref<Map<string, Reason>>(new Map());
  const itemsForbiddenToEdit = ref<Map<string, Reason>>(new Map());
  const itemsForbiddenToDelete = ref<Map<string, Reason>>(new Map());
  watch(
    callerAdminEmployeeOrganizations,
    async () => {
      itemsForbiddenToCreate.value = new Map();
      itemsForbiddenToEdit.value = new Map();
      itemsForbiddenToDelete.value = new Map();

      const organizationWithCreateUserResult = callerAdminEmployeeOrganizations.value
        .filter((org) => !isSelected(org))
        .map(async (org): Promise<Entities> => [org.uid, await isUsersForbiddenToCreate(org)]);
      createItemsForbiddenMap(Promise.all(organizationWithCreateUserResult)).then((map) => {
        itemsForbiddenToCreate.value = map;
      });

      const associationUpdatePermissionResults = callerAdminEmployeeOrganizations.value
        .filter((org) => isSelected(org))
        .map(async (org) => [org.uid, await isAssociationUpdatable(targetUser, org)] as [string, PermissionResult]);
      createItemsForbiddenMap(Promise.all(associationUpdatePermissionResults)).then((map) => {
        itemsForbiddenToEdit.value = map;
      });

      const organizationWithDeletePermissionResult = callerAdminEmployeeOrganizations.value
        .filter((org) => isSelected(org))
        .map(
          async (org) =>
            [org.uid, await isUserAssociationForbiddenToDelete(targetUser, org)] as [string, PermissionResult],
        );
      createItemsForbiddenMap(Promise.all(organizationWithDeletePermissionResult)).then((map) => {
        itemsForbiddenToDelete.value = map;
      });
    },
    { immediate: true },
  );

  return {
    callerOrganizations: callerAdminEmployeeOrganizations,
    itemsForbiddenToCreate,
    itemsForbiddenToEdit,
    itemsForbiddenToDelete,
    createItemsForbiddenMap,
    isUsersForbiddenToCreate,
    isAssociationUpdatable,
    isUserAssociationForbiddenToDelete,
  };
};
